import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Text,
  Button,
  Textarea,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Toolbar,
  ToolbarButton,
  Tooltip,
  Spinner,
} from "@fluentui/react-components";
import {
  Key24Regular,
  ArrowLeft20Regular,
  Checkmark24Regular,
} from "@fluentui/react-icons";
import { useDevice } from "../../../model";
import { useAppStyles } from "../../styles/appStyles";
import { config } from "../../../config/environment";
import { CarboWasteEndpoints_Registration } from "../../../api/endpoints";

interface ActivationPageProps {
  onBack: () => void;
  onSuccess?: () => void;
}

const ActivationPage: React.FC<ActivationPageProps> = ({ onBack, onSuccess }) => {
  const { t } = useTranslation();
  const styles = useAppStyles();
  const { fingerprint, refreshSubscription } = useDevice();

  const [activationKey, setActivationKey] = useState("");
  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleActivate = async () => {
    const trimmedKey = activationKey.trim();

    if (!trimmedKey) {
      setError(t("activation.error.key_required", "Klucz aktywacyjny jest wymagany"));
      return;
    }

    setIsActivating(true);
    setError(null);

    try {
      console.log("🔑 Starting activation...");

      // Zbieranie informacji o urządzeniu
      const platform = navigator.platform || "Unknown";
      const userAgent = navigator.userAgent || "Unknown";
      const host = window.location.hostname || "Unknown";

      let officeVersion = "Unknown";
      try {
        if (Office.context && Office.context.diagnostics) {
          officeVersion = Office.context.diagnostics.version || "Unknown";
        }
      } catch (err) {
        console.warn("Could not retrieve Office version:", err);
      }

      const payload = {
        activationKey: trimmedKey,
        deviceFingerprint: fingerprint,
        platform,
        host,
        userAgent,
        officeVersion,
      };

      console.log("📡 Calling registration API...");

      const response = await fetch(CarboWasteEndpoints_Registration, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMessage = t("activation.error.unknown", "Nieznany błąd aktywacji");

        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            console.error("❌ API Error response:", errorData);

            // Format błędu z API: { "error": "string", "details": "string" }
            if (errorData.details) {
              errorMessage = errorData.details;
            } else if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          } else {
            errorMessage = `${t("activation.error.http", "Błąd HTTP")} ${response.status}`;
          }
        } catch (parseError) {
          console.error("❌ Error parsing error response:", parseError);
          errorMessage = `${t("activation.error.http", "Błąd HTTP")} ${response.status}`;
        }

        throw new Error(errorMessage);
      }

      const subscription = await response.json();
      console.log("✅ Activation successful:", subscription);

      setSuccess(true);
      setActivationKey("");

      // Odśwież subskrypcję
      await refreshSubscription();

      // Po krótkiej chwili wróć lub wywołaj callback sukcesu
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        } else {
          onBack();
        }
      }, 2000);
    } catch (err) {
      console.error("❌ Activation error:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsActivating(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey && !isActivating) {
      handleActivate();
    }
  };

  return (
    <div className={styles.appPageContainer}>
      <div className={styles.appPageHeader}>
        <Key24Regular className={styles.appPageHeaderIcon} />
        <Text className={styles.appPageTitle}>
          {t("activation.title", "Aktywacja aplikacji")}
        </Text>
      </div>

      <Toolbar className={styles.toolbar}>
        <Tooltip
          content={t("activation.toolbar.back_tooltip", "Powrót do poprzedniej strony")}
          relationship="description"
        >
          <ToolbarButton
            className={styles.toolbarButton}
            aria-label={t("activation.toolbar.back", "Powrót")}
            icon={<ArrowLeft20Regular className={styles.backIcon} />}
            onClick={onBack}
            disabled={isActivating}
          >
            {t("activation.toolbar.back", "Powrót")}
          </ToolbarButton>
        </Tooltip>
      </Toolbar>

      <div className={styles.appPageContent}>
        {/* Success Message */}
        {success && (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>
                {t("activation.success.title", "Aktywacja pomyślna!")}
              </MessageBarTitle>
              {t(
                "activation.success.message",
                "Aplikacja została pomyślnie aktywowana. Przekierowywanie..."
              )}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Error Message */}
        {error && !success && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>{t("activation.error.title", "Błąd aktywacji")}</MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Instructions */}
        {!success && (
          <>
            <div style={{ marginBottom: "16px" }}>
              <Text>
                {t(
                  "activation.description",
                  "Wprowadź klucz aktywacyjny otrzymany od administratora lub z zakupu subskrypcji."
                )}
              </Text>
            </div>

            {/* Activation Key Input */}
            <div style={{ marginBottom: "16px" }}>
              <Text weight="semibold" style={{ display: "block", marginBottom: "8px" }}>
                {t("activation.key_label", "Klucz aktywacyjny")}
                <span style={{ color: "#a80000" }}> *</span>
              </Text>
              <Textarea
                value={activationKey}
                onChange={(e, data) => setActivationKey(data.value)}
                onKeyDown={handleKeyPress}
                placeholder={t("activation.key_placeholder", "Wklej klucz subskrypcji (maks. 256 znaków)")}
                resize="none"
                rows={5}
                maxLength={256}
                disabled={isActivating}
                style={{ fontFamily: "monospace", width: "100%" }}
              />
              <Text size={100} style={{ color: "#605e5c", marginTop: "4px", display: "block" }}>
                {t("activation.key_length", "Długość")}: {activationKey.length} / 256{" "}
                {t("activation.key_characters", "znaków")}
              </Text>
            </div>

            {/* Activation Button */}
            <div style={{ marginTop: "24px" }}>
              <Button
                appearance="primary"
                onClick={handleActivate}
                disabled={isActivating || !activationKey.trim()}
                icon={isActivating ? <Spinner size="tiny" /> : undefined}
                style={{ width: "100%" }}
              >
                {isActivating
                  ? t("activation.button.activating", "Aktywowanie...")
                  : t("activation.button.activate", "Aktywuj")}
              </Button>
            </div>

          </>
        )}
      </div>
    </div>
  );
};

export default ActivationPage;
